// api/bchAccount.js
import { apiGet } from "./http";

/**
 * =========================
 * BCH 账户资金管理
 * =========================
 */

/**
 * 查询 BCH 账户概览
 *
 * GET /app/bch/account/overview
 *
 * 返回内容：
 * - coinType
 * - availableBalance
 * - frozenAmount
 * - totalCashAmount
 * - pendingAmount
 * - categories
 * - tradeTypes
 */
export function fetchBchAccountOverview(config = {}) {
  return apiGet(
    "/app/bch/account/overview",
    {},
    {
      silent: true,
      ...config,
    }
  );
}

/**
 * 分页查询 BCH 账户流水
 *
 * GET /app/bch/account/details
 *
 * 参数：
 * - current: 当前页
 * - size: 每页条数
 * - category:
 *      all       全部
 *      income    收益
 *      withdraw  提现
 *      team      团队
 *      direct    直推
 * - tradeType: 具体交易类型，可选
 * - state:
 *      1 审核中
 *      2 处理中
 *      3 成功
 *      4 失败
 * - startDate: 开始日期，格式 YYYY-MM-DD
 * - endDate: 结束日期，格式 YYYY-MM-DD
 */
export function fetchBchAccountDetails(
  params = {},
  config = {}
) {
  const {
    current = 1,
    size = 20,
    category = "all",
    tradeType,
    state,
    startDate,
    endDate,
  } = params || {};

  const query = {
    current,
    size,
    category,
  };

  // 可选参数只有有值时才提交，避免传递空字符串
  if (tradeType) {
    query.tradeType = tradeType;
  }

  if (
    state !== undefined &&
    state !== null &&
    state !== ""
  ) {
    query.state = state;
  }

  if (startDate) {
    query.startDate = startDate;
  }

  if (endDate) {
    query.endDate = endDate;
  }

  return apiGet(
    "/app/bch/account/details",
    query,
    {
      silent: true,
      ...config,
    }
  );
}

/**
 * 查询全部账户流水
 */
export function fetchAllBchAccountDetails(
  params = {},
  config = {}
) {
  return fetchBchAccountDetails(
    {
      ...params,
      category: "all",
    },
    config
  );
}

/**
 * 查询收益流水
 *
 * 包括：
 * - BCH_ARBITRAGE_DAILY_PROFIT
 * - BCH_STANDARD_PRINCIPAL_RELEASE
 * - BCH_TRIAL_PRINCIPAL_RETURN
 * - BCH_TRIAL_PROFIT
 */
export function fetchBchIncomeDetails(
  params = {},
  config = {}
) {
  return fetchBchAccountDetails(
    {
      ...params,
      category: "income",
    },
    config
  );
}

/**
 * 查询提现流水
 *
 * 包括：
 * - BCH_WITHDRAW
 * - BCH_WITHDRAW_FEE
 */
export function fetchBchWithdrawDetails(
  params = {},
  config = {}
) {
  return fetchBchAccountDetails(
    {
      ...params,
      category: "withdraw",
    },
    config
  );
}

/**
 * 查询团队奖励流水
 *
 * 包括：
 * - BCH_TEAM_LEVEL_DIFF_REWARD
 */
export function fetchBchTeamRewardDetails(
  params = {},
  config = {}
) {
  return fetchBchAccountDetails(
    {
      ...params,
      category: "team",
    },
    config
  );
}

/**
 * 查询直推奖励流水
 *
 * 包括：
 * - BCH_STANDARD_DIRECT_REWARD
 */
export function fetchBchDirectRewardDetails(
  params = {},
  config = {}
) {
  return fetchBchAccountDetails(
    {
      ...params,
      category: "direct",
    },
    config
  );
}